import React from 'react';
import { Button } from '../components/ui/Button';
import { cn } from '../utils/cn';
import { useLanguage } from '../contexts/LanguageContext';

export const ResultsScreen = ({ data, onPlayAgain, onGoHome }) => {
    const { t } = useLanguage();
    const results = Array.isArray(data?.results) ? data.results : [];
    const votes = Array.isArray(data?.votes) ? data.votes : [];

    const botPlayer = results.find(r => r.was_bot);
    const botDisplayName = botPlayer ? `${t('game_player')} ${results.indexOf(botPlayer) + 1}` : t('game_bot');

    const getLeagueIcon = (league) => {
        const icons = { wooden: '🪵', bronze: '🥉', silver: '🥈', gold: '🥇' };
        return icons[league?.toLowerCase()] || '🏅';
    };

    const getLeagueName = (league) => {
        const key = `league_${league?.toLowerCase()}`;
        return t(key) || league;
    };


    return (
        <div className="flex flex-col h-full w-full px-4 pt-6 pb-10 space-y-6 overflow-hidden">
            <div className="text-center space-y-2 shrink-0">
                <h1 className="text-2xl font-black text-white uppercase tracking-tighter text-glow italic">
                    {t('results_title')}
                </h1>
                <div className="inline-block px-4 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 text-xs font-bold uppercase tracking-widest">
                    {botPlayer ? t('results_bot_revealed').replace('{name}', botDisplayName) : t('results_bot_hidden')}
                </div>
            </div>

            {/* Players Table */}
            <div className="flex-1 overflow-y-auto space-y-3 px-1 custom-scrollbar">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-2 px-1">
                    {t('results_stats')}
                </div>
                {results.map((player, idx) => {
                    const tg = window.Telegram?.WebApp;
                    const isMe = tg?.initDataUnsafe?.user?.id?.toString() === player.player_id;
                    const deltaSign = player.rating_delta >= 0 ? '+' : '';
                    const isPositive = player.rating_delta >= 0;

                    return (
                        <div
                            key={player.player_id}
                            className={cn(
                                "p-4 rounded-2xl border flex items-center gap-4 transition-all",
                                player.was_bot
                                    ? "bg-red-500/10 border-red-500/30"
                                    : isMe
                                        ? "bg-cyan-500/10 border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.1)]"
                                        : "bg-white/5 border-white/10"
                            )}
                        >
                            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-lg shadow-inner">
                                {player.avatar_url ? (
                                    <img src={player.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-700 text-white rounded-full">
                                        {player.was_bot ? (
                                            <span className="text-2xl filter drop-shadow-md">🤖</span>
                                        ) : (
                                            <span className="font-mono text-sm font-bold">
                                                {(player.nickname || `P${idx + 1}`).substring(0, 2).toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className={cn(
                                        "text-sm font-bold truncate",
                                        isMe ? "text-cyan-400" : "text-white"
                                    )}>
                                        {isMe ? t('game_you').toUpperCase() : (player.nickname || `${t('game_player')} ${idx + 1}`)}
                                        <span className="ml-2 text-[10px] text-slate-500 font-normal uppercase tracking-tight">
                                            ({t('game_player')} {idx + 1})
                                        </span>
                                    </span>
                                    {player.was_bot && (
                                        <span className="px-1.5 py-0.5 rounded bg-red-500 text-[8px] font-black text-white uppercase italic">{t('results_bot_badge')}</span>
                                    )}
                                    {player.guessed_bot && !player.was_bot && (
                                        <span className="px-1.5 py-0.5 rounded bg-green-500 text-[8px] font-black text-white uppercase">{t('results_guessed')}</span>
                                    )}
                                </div>
                                {!player.was_bot && (
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider bg-white/5 px-1.5 py-0.5 rounded">
                                            {getLeagueIcon(player.league)} {getLeagueName(player.league)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {!player.was_bot && (
                                <div className={cn(
                                    "text-lg font-black font-mono",
                                    isPositive ? "text-green-400" : "text-red-400"
                                )}>
                                    {deltaSign}{player.rating_delta}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Votes Summary */}
                {votes.length > 0 && (
                    <div className="mt-8 space-y-3">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] px-1">
                            {t('results_vote_summary')}
                        </div>
                        <div className="p-4 rounded-2xl bg-white/2 border border-white/5 space-y-2">
                            {votes.map((vote, i) => {
                                const voterIdx = results.findIndex(r => r.player_id === vote.voter_id);
                                const suspectIdx = vote.suspected_id ? results.findIndex(r => r.player_id === vote.suspected_id) : -1;

                                return (
                                    <div key={i} className="flex items-center justify-between text-[11px] font-medium border-b border-white/5 pb-2 last:border-0 last:pb-0">
                                        <span className="text-slate-400">{t('game_player')} {voterIdx + 1}</span>
                                        <span className="text-slate-500 px-2 text-[10px]">{t('results_vote_against')}</span>
                                        <span className={cn(
                                            "font-bold",
                                            suspectIdx >= 0 ? "text-white" : "text-slate-500"
                                        )}>
                                            {suspectIdx >= 0 ? `${t('game_player')} ${suspectIdx + 1}` : t('results_vote_skipped')}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-4 shrink-0 px-2">
                <Button
                    variant="secondary"
                    onClick={onGoHome}
                    className="h-14 rounded-2xl"
                >
                    {t('results_menu')}
                </Button>
                <Button
                    variant="primary"
                    onClick={onPlayAgain}
                    className="h-14 rounded-2xl shadow-[0_0_20px_rgba(6,182,212,0.3)]"
                >
                    {t('results_again')}
                </Button>
            </div>
        </div>
    );
};
